export interface BookingExtra {
  name: string;
  price_cents: number;
}

export interface Booking {
  id: number;
  tenant_id: number;

  // Booking number
  booking_number: string;

  // Relations
  customer_id: number;
  vehicle_id: number;

  // Time period
  start_date: string;
  start_time: string | null;
  end_date: string;
  end_time: string | null;

  // Pickup/Return
  pickup_location: string | null;
  return_location: string | null;

  // Price calculation
  base_price_cents: number;
  extras_price_cents: number;
  insurance_price_cents: number;
  total_price_cents: number;

  // Extras
  extras: BookingExtra[];

  // Mileage
  planned_km: number | null;

  // Deposit
  deposit_cents: number | null;
  deposit_paid: boolean;
  deposit_payment_method: string | null;

  // Status
  status: 'pending' | 'confirmed' | 'picked_up' | 'returned' | 'completed' | 'cancelled';

  // Cancellation
  cancelled_at: string | null;
  cancelled_by: string | null;
  cancellation_reason: string | null;

  // Payment status
  payment_status: 'unpaid' | 'partial' | 'paid';
  paid_amount_cents: number;

  // Notes
  customer_notes: string | null;
  internal_notes: string | null;

  // Online booking
  is_online_booking: boolean;

  // Timestamps
  created_at: string;
  updated_at: string;
  created_by: string | null;

  // Related data (optional)
  customer_name?: string;
  vehicle_name?: string;
}

export interface BookingCreate {
  booking_number: string;
  customer_id: number;
  vehicle_id: number;
  start_date: string;
  start_time?: string;
  end_date: string;
  end_time?: string;
  pickup_location?: string;
  return_location?: string;
  base_price_cents: number;
  extras_price_cents?: number;
  insurance_price_cents?: number;
  total_price_cents: number;
  extras?: BookingExtra[];
  planned_km?: number;
  deposit_cents?: number;
  deposit_paid?: boolean;
  deposit_payment_method?: string;
  status?: 'pending' | 'confirmed' | 'picked_up' | 'returned' | 'completed' | 'cancelled';
  payment_status?: 'unpaid' | 'partial' | 'paid';
  paid_amount_cents?: number;
  customer_notes?: string;
  internal_notes?: string;
  is_online_booking?: boolean;
}

export type BookingUpdate = Partial<BookingCreate>;

export interface BookingListResponse {
  bookings: Booking[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface AvailabilityCheckRequest {
  vehicle_id: number;
  start_date: string;
  end_date: string;
}

export interface AvailabilityCheckResponse {
  vehicle_id: number;
  is_available: boolean;
  conflicting_bookings: number[];
}

// Helper function to format booking date range
export function formatBookingDateRange(booking: Booking): string {
  const startDate = new Date(booking.start_date).toLocaleDateString('de-DE');
  const endDate = new Date(booking.end_date).toLocaleDateString('de-DE');
  return `${startDate} - ${endDate}`;
}

// Helper function to calculate booking duration in days
export function calculateBookingDays(startDate: string, endDate: string): number {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays || 1;
}

// Helper function to format price from cents to EUR
export function formatPrice(cents: number): string {
  return (cents / 100).toFixed(2) + ' €';
}

// Status translations
export const STATUS_LABELS: Record<Booking['status'], string> = {
  pending: 'Ausstehend',
  confirmed: 'Bestätigt',
  picked_up: 'Abgeholt',
  returned: 'Zurückgegeben',
  completed: 'Abgeschlossen',
  cancelled: 'Storniert',
};

// Status colors for badges
export const STATUS_COLORS: Record<Booking['status'], string> = {
  pending: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  confirmed: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  picked_up: 'bg-green-500/15 text-green-600 dark:text-green-400',
  returned: 'bg-purple-500/15 text-purple-600 dark:text-purple-400',
  completed: 'bg-muted text-muted-foreground',
  cancelled: 'bg-red-500/15 text-red-600 dark:text-red-400',
};
